import { Button } from '@/components/ui/shadcn/button';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from '@/components/ui/shadcn/dialog';
import { useTranslations } from '@/hooks/useTranslations';
import { Check, RotateCcw, SlidersHorizontal, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { FilterPanel, filterIcon, type ToolbarFilterOption } from './FilterPanel';

interface FilterModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** The toolbar button that opens the modal. */
    trigger: ReactNode;
    title: string;
    resourceNamePlural: string;
    filteredCount: number;
    filterDefinitions: Array<{ id: string; label: string }>;
    columnFilterOptions: Record<string, ToolbarFilterOption[]>;
    columnFilterLoading?: Record<string, boolean>;
    columnFilters: Record<string, string[]>;
    columnFilterSearch: Record<string, string>;
    setColumnFilterSearch: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    toggleSingleFilter: (columnId: string, value: string) => void;
    handleFilterChange: (column: string, values: string[]) => void;
    clearFilters: () => void;
    /** Optional custom controls rendered as the first card (e.g. a date range). */
    header?: ReactNode;
    footer?: ReactNode;
}

/**
 * Spacious, card-based filter modal. Each filter is its own card in a responsive grid, with a
 * rich header (icon + title + subtitle), active-filter chips + Reset, and a Clear / Apply footer.
 * Filtering is applied live (server-side); the footer surfaces the running result count.
 */
export function FilterModal({
    open,
    onOpenChange,
    trigger,
    title,
    resourceNamePlural,
    filteredCount,
    filterDefinitions,
    columnFilterOptions,
    columnFilterLoading,
    columnFilters,
    columnFilterSearch,
    setColumnFilterSearch,
    toggleSingleFilter,
    handleFilterChange,
    clearFilters,
    header,
    footer,
}: FilterModalProps) {
    const { t } = useTranslations();

    const activeChips = filterDefinitions.flatMap((filter) => {
        const options = columnFilterOptions[filter.id] || [];
        return (columnFilters[filter.id] || []).map((value) => ({
            columnId: filter.id,
            filterLabel: filter.label,
            value,
            label: options.find((option) => option.value === value)?.label ?? value,
        }));
    });

    const hasActiveFilters = activeChips.length > 0;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>{trigger}</DialogTrigger>
            <DialogContent
                showCloseButton={false}
                className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl"
            >
                {/* Header */}
                <div className="flex items-start gap-3 border-b border-border px-6 py-5">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <SlidersHorizontal className="h-5 w-5" />
                    </span>
                    <div className="min-w-0 flex-1">
                        <DialogTitle className="text-xl font-semibold">{title}</DialogTitle>
                        <DialogDescription className="mt-0.5">
                            {t('common.filter_description', 'Filter records by different criteria')}
                        </DialogDescription>
                    </div>
                    <DialogClose asChild>
                        <Button variant="outline" size="icon" className="h-9 w-9 shrink-0 rounded-lg">
                            <X className="h-4 w-4" />
                            <span className="sr-only">{t('common.close', 'Close')}</span>
                        </Button>
                    </DialogClose>
                </div>

                {/* Active-filter chips + Reset */}
                {hasActiveFilters && (
                    <div className="flex flex-wrap items-center gap-2 border-b border-border px-6 py-3">
                        {activeChips.map((chip) => (
                            <span
                                key={`${chip.columnId}-${chip.value}`}
                                className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-border bg-muted/40 py-1 pr-1 pl-2.5 text-sm"
                            >
                                <span className="shrink-0 text-muted-foreground [&_svg]:h-3.5 [&_svg]:w-3.5">
                                    {filterIcon(chip.columnId)}
                                </span>
                                <span className="truncate" title={`${chip.filterLabel}: ${chip.label}`}>
                                    {chip.filterLabel}: {chip.label}
                                </span>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => toggleSingleFilter(chip.columnId, chip.value)}
                                    className="h-5 w-5 shrink-0 rounded-md p-0 text-muted-foreground hover:bg-background hover:text-foreground"
                                    aria-label={`${t('common.reset', 'Reset')} ${chip.filterLabel}`}
                                >
                                    <X className="h-3.5 w-3.5" />
                                </Button>
                            </span>
                        ))}
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={clearFilters}
                            className="ml-auto gap-1.5 text-primary hover:bg-primary/10 hover:text-primary"
                        >
                            <RotateCcw className="h-4 w-4" />
                            {t('common.reset', 'Reset')}
                        </Button>
                    </div>
                )}

                {/* Cards */}
                <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                    <FilterPanel
                        filterDefinitions={filterDefinitions}
                        columnFilterOptions={columnFilterOptions}
                        columnFilterLoading={columnFilterLoading}
                        columnFilters={columnFilters}
                        columnFilterSearch={columnFilterSearch}
                        setColumnFilterSearch={setColumnFilterSearch}
                        toggleSingleFilter={toggleSingleFilter}
                        handleFilterChange={handleFilterChange}
                        header={header}
                        footer={footer}
                    />
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-2 border-t border-border px-6 py-4">
                    <span className="text-sm text-muted-foreground">
                        {filteredCount} {resourceNamePlural} {t('common.found', 'found')}
                    </span>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={clearFilters}>
                            {t('common.clear', 'Clear')}
                        </Button>
                        <Button onClick={() => onOpenChange(false)} className="gap-1.5">
                            <Check className="h-4 w-4" />
                            {t('common.apply_filters', 'Apply filters')}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
